import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useProducts } from '../../hooks/useProducts';

import styles from '../../css/Store.module.css';

import { HighlightButton, SecondaryButton } from '../HighlightButton/HighlightButton';
import ProductInfoChips from '../ProductInfoChips/ProductInfoChips';
import CheckoutForm from './CheckoutForm';
import { ANALYTICS_EVENTS, ERROR_MESSAGES } from './utils/constants';

const Store = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const { products, loading, error } = useProducts();  // fetch from Context

  // Find the current product
  const item = useMemo(() => {
    const result = products.find(product => product.id === id);
    return result;
  }, [products, id]);

  const renderPurchaseBanner = () => {
    if (!item) return;

    return (
      <header className={styles.purchaseBanner}>
        <div className={styles.purchaseBannerButtonsContainer}>
          <SecondaryButton onClick={handleBackToCatalog}>
            <p className={styles.storeButtonText}>BACK</p>
          </SecondaryButton>

          <HighlightButton onClick={handlePurchaseClick}>
            <p>PURCHASE FOR ${parseFloat(item.price).toFixed(2)}</p>
          </HighlightButton>
        </div>
      </header>
    );
  };

  const imgUrl = (id, width) => {
    return `/static/products/${id}/${id}_${width}w.webp ${width}w`;
  }

  const renderYoutubeEmbed = () => {
    if (!item.youtube) return null;

    const videoId = item.youtube.includes('youtu.be/')
      ? item.youtube.split('youtu.be/')[1]
      : item.youtube.split('v=')[1]?.split('&')[0];

    const params = new URLSearchParams({
      rel: 0,              // related from same channel
      modestbranding: 1,   // smaller YouTube logo
      iv_load_policy: 3,   // hide annotations/cards
      playsinline: 1,      // inline on iOS
      fs: 0,               // hide fullscreen button
      controls: 0,      // uncomment to hide controls (you must provide your own)
      enablejsapi: 1,
      origin: window.location.origin,
    }).toString();

    return (
      <div className={styles.youtubeEmbedContainer}>
        <iframe
          className={styles.youtubeEmbed}
          width="100%"
          height="315"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?${params}`}
          title={`${item.title} - YouTube Video`}
          frameBorder="0"
          allow="encrypted-media"
        />
      </div>
    );
  }

  const handleAudioPlay = async () => {
    if (item) {
      const { logAnalyticsEvent } = await import('../../utils/analyticsUtils');
      logAnalyticsEvent(ANALYTICS_EVENTS.AUDIO_PLAY, {
        product_id: item.id,
        product_title: item.title
      });
    }
  };

  const handlePurchaseClick = () => {
    setShowCheckout(true);
  };

  const handleBackToCatalog = () => {
    navigate('/work');
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loadingContainer}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.errorContainer}>
          <div style={{ paddingVertical: '2rem' }}>
            <p>An error occurred.</p>
            {/* <p>{error}</p> */}
            <SecondaryButton onClick={handleBackToCatalog}>Back to Catalog</SecondaryButton>
          </div>
        </div>
      </div>
    );
  }

  // If item not found
  if (!item) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.heroSection}>
          <h1>Item Not Found</h1>
          <p>{ERROR_MESSAGES.ITEM_NOT_FOUND}</p>
          <SecondaryButton onClick={handleBackToCatalog}>Back to Catalog</SecondaryButton>
        </div>
      </div>
    );
  }

  // Normal product display
  return (
    <main>
      {!showCheckout && renderPurchaseBanner()}

      <div className={styles.pageContainer}>
        <div className={`${styles.mainSection} ${styles.stack}`}>
          <section className={styles.checkoutContainer} style={{ 'display': showCheckout ? 'block' : 'none' }}>
            <div className={styles.checkoutHeader}>
              <SecondaryButton onClick={() => setShowCheckout(false)}>Back To Product Info</SecondaryButton>
            </div>
            <CheckoutForm item={item} />
          </section>

          {!showCheckout && <article className={styles.stack}>
            {/* Don't show title for now
            <div className={styles.heroSection}>
              <h1>{item.title}</h1>
            </div> */}

            <ProductInfoChips item={item} ensemble parts performers difficulty level />

            <section className={styles.previewSection}>
              <div className={styles.previewImageContainer}>
                <img
                  className={styles.previewImage}
                  src={`/static/products/${item.id}/${item.id}_1200w.webp`}
                  srcSet={`${imgUrl(item.id, 2400)}, ${imgUrl(item.id, 1600)}, ${imgUrl(item.id, 1200)}, ${imgUrl(item.id, 768)}, ${imgUrl(item.id, 480)}`}
                  sizes="(max-width: 768px) 100vw, 60vw"
                  alt={`Sheet music preview of ${item.title}`}
                  fetchpriority="high"
                />
              </div>
            </section>

            <section>
              {item.youtube
                ? renderYoutubeEmbed()
                : <div className={styles.audioPlayerContainer}>
                  <audio
                    className={styles.audioPlayer}
                    controls
                    controlsList="nodownload noplaybackrate nomute notimeremaining"
                    onPlay={handleAudioPlay}
                  >
                    <source src={`/static/products/${item.id}/${item.id}.mp3`} type="audio/mp3" />
                  </audio>
                </div>
              }
            </section>

            <section className={styles.descriptionSection}>
              <p>{item.description}</p>
            </section>

            <ProductInfoChips item={item} numberofpages duration />

            {/* Page looks better without this, but probably optimal to have
            <section className={styles.ctaSection}>
              <HighlightButton onClick={handlePurchaseClick}>
                <p>PURCHASE</p>
              </HighlightButton>
            </section> */}

            <section className={styles.questionsSection}>
              <span>Questions?</span>
              <span style={{ marginRight: '3px' }}></span>
              <Link to="/contact" target="_blank" rel="noopener noreferrer" aria-label="Contact Me (opens in new tab)">
                Contact Me
              </Link>
            </section>
          </article>
          }
        </div>
      </div>
    </main>
  );
};

export default Store;
